"use client";

import { ShoppingCart, Package, Gift, Heart } from "lucide-react";
import { useEffect, useState } from "react";

export default function GiftOrderProcess() {
  const steps = [
    { 
      title: "Choose Gift", 
      desc: "Browse and select the perfect gift for your loved one.", 
      icon: <ShoppingCart size={38} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
    },
    { 
      title: "Gift Wrapping", 
      desc: "We beautifully wrap your gift with care and attention.", 
      icon: <Gift size={38} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FF8E53 0%, #FFB347 100%)",
    },
    { 
      title: "Fast Delivery", 
      desc: "Express shipping to ensure timely arrival.", 
      icon: <Package size={38} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FFB347 0%, #FFC872 100%)",
    },
    { 
      title: "Smiles Delivered", 
      desc: "Your gift brings joy and creates happy memories.", 
      icon: <Heart size={38} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FF6B9D 0%, #FF8EB3 100%)",
    },
  ];

  const [sparkles, setSparkles] = useState<
    { left: number; size: number; duration: number; delay: number; emoji: string }[]
  >([]);

  useEffect(() => {
    const count = 30;
    const emojis = ["🎁", "✨", "🎀", "💝", "⭐"];
    const arr = Array.from({ length: count }).map(() => ({
      left: Math.random() * 100,
      size: 14 + Math.random() * 10,
      duration: 6 + Math.random() * 8,
      delay: Math.random() * 5,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
    }));
    setSparkles(arr);
  }, []);

  const stepCard: React.CSSProperties = {
    textAlign: "center",
    padding: "32px 24px",
    borderRadius: "24px",
    background: "#FFFFFF",
    border: "2px solid #FFF0E6",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: `
      0 2px 4px rgba(255, 255, 255, 0.08),
      0 4px 8px rgba(255,140,83,0.1),
      0 8px 16px rgba(255,140,83,0.12),
      0 16px 32px rgba(63, 63, 63, 0.08)
    `,
    color: "#2C2C2C",
    position: "relative",
    overflow: "hidden",
  };

  const css = `
    @keyframes sparkleFloat {
      0% { 
        transform: translateY(-5vh) rotate(0deg); 
        opacity: 0; 
      }
      10% { 
        opacity: 0.8; 
      }
      50% { 
        transform: translateY(50vh) rotate(180deg); 
        opacity: 0.9; 
      }
      90% { 
        opacity: 0.6; 
      }
      100% { 
        transform: translateY(110vh) rotate(360deg); 
        opacity: 0; 
      }
    }

    @media(max-width: 768px){ 
      .order-grid{ 
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 16px !important;
      }
      .step-card {
        padding: 24px 16px !important;
      }
      .icon-box {
        width: 64px !important;
        height: 64px !important;
        margin-bottom: 12px !important;
      }
      .step-title {
        font-size: 17px !important;
      }
      .step-desc {
        font-size: 13px !important;
      }
    }
    @media(min-width: 769px){ 
      .order-grid{ 
        grid-template-columns: repeat(4, 1fr) !important; 
      }
    }

    .step-card::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,182,193,0.15), transparent);
      transition: left 0.6s ease;
    }

    .step-card:hover::before {
      left: 100%;
    }

    .step-card:hover {
      transform: translateY(-8px) scale(1.03);
      box-shadow: 
        0 4px 8px rgba(255,107,107,0.12),
        0 8px 16px rgba(255,140,83,0.15),
        0 16px 32px rgba(255,107,107,0.18),
        0 32px 64px rgba(255,107,107,0.12);
      border-color: #FFD4BA;
    }

    .step-card:hover .icon-box {
      transform: scale(1.15) rotate(5deg);
    }

    .step-number {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
      color: white;
      display: grid;
      place-items: center;
      font-size: 14px;
      font-weight: 800;
      box-shadow: 0 2px 8px rgba(255,107,107,0.3);
    }
  `;

  return (
    <>
      <style>{css}</style>

      <section
        style={{
          position: "relative",
          padding: "80px 20px",
          background: "linear-gradient(135deg, #FFF9F5 0%, #FFFBF8 50%, #FFF5EE 100%)",
          overflow: "hidden",
        }}
      >
        {/* Decorative backgrounds */}
        <div
          style={{
            position: "absolute",
            top: "-80px",
            right: "-120px",
            width: "350px",
            height: "350px",
            background: "radial-gradient(circle, rgba(255,182,193,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-60px",
            left: "-100px",
            width: "300px",
            height: "300px",
            background: "radial-gradient(circle, rgba(255,215,180,0.12) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />

        {/* Floating sparkles */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          {sparkles.map((s, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                top: "-5vh",
                left: `${s.left}%`,
                fontSize: `${s.size}px`,
                animation: `sparkleFloat ${s.duration}s linear infinite`,
                animationDelay: `${s.delay}s`,
              }}
            >
              {s.emoji}
            </div>
          ))}
        </div>

        <div 
          style={{ 
            maxWidth: "1400px", 
            margin: "0 auto", 
            textAlign: "center", 
            position: "relative", 
            zIndex: 2 
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: "12px" }}>
            <span style={{ fontSize: "32px", display: "inline-block" }}>🎁</span>
          </div>

          <h2 
            style={{ 
              fontSize: "48px", 
              fontWeight: 900, 
              color: "#2C2C2C", 
              marginBottom: "16px",
              letterSpacing: "-1px",
            }}
          >
            How Gift Giving{" "}
            <span 
              style={{ 
                background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Works
            </span>
          </h2>

          <p
            style={{
              color: "#666",
              fontSize: "18px",
              marginBottom: "50px",
              maxWidth: "700px",
              marginInline: "auto",
              lineHeight: 1.7,
              fontWeight: 500,
            }}
          >
            From selection to smiles – we make every step of gifting magical and memorable
          </p>

          <div 
            className="order-grid" 
            style={{ 
              display: "grid", 
              gap: "28px", 
              gridTemplateColumns: "repeat(4, 1fr)" 
            }}
          >
            {steps.map((step, i) => (
              <div key={i} className="step-card" style={stepCard}>
                <div className="step-number">{i + 1}</div>

                <div 
                  className="icon-box" 
                  style={{
                    width: "80px",
                    height: "80px",
                    margin: "0 auto 18px auto",
                    borderRadius: "20px",
                    background: step.gradient,
                    color: "#fff",
                    display: "grid",
                    placeItems: "center",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: `0 4px 16px ${step.gradient.includes('FF6B6B') ? 'rgba(255,107,107,0.3)' : 'rgba(255,140,83,0.25)'}`,
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {step.icon}
                </div>

                <h3 
                  className="step-title"
                  style={{ 
                    fontSize: "20px", 
                    fontWeight: 800, 
                    color: "#2C2C2C",
                    marginBottom: "10px",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {step.title}
                </h3>

                <p 
                  className="step-desc"
                  style={{ 
                    fontSize: "14px", 
                    color: "#666", 
                    lineHeight: 1.6,
                    fontWeight: 500,
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}